import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useHistory, Switch, Route } from 'react-router-dom';
import { toast } from 'react-toastify';
import Users from './Users';
import Dashboard from './Dashboard';
import Sidebar from '../components/Sidebar';
import TopBar, { PrimaryAction } from '../components/TopBar';
import { PageMetaProvider } from '../context/PageMeta';
import OrderPopup from '../components/modals/OrderPopup';
import useOrderByPid, {
  OpenableOrderKind,
} from '../hooks/reactQuery/useOrderByPid';
import RightSideModal from '../components/modals/RightSideModal';
import OrderDetailsModal from '../components/modals/OrderDetailsUpdateModal';
import UserDetailsModal from '../components/modals/UserDetailsModal';
import UserProfileSettings from './Settings';
import AskAi from './AskAi';
import Schedules from './Schedules';
import useUserUpdate from '../hooks/reactQuery/useUpdateUser';
import CreateOrderModal from '../components/modals/CreateOrderModal';
import useCreateOrder from '../hooks/reactQuery/useCreateOrder';
import CreateThunderbirdOrderModal from '../components/modals/CreateThunderbirdOrderModal';
import useCreateThunderbirdOrder from '../hooks/reactQuery/useCreateThunderbirdOrder';
import useCreateUser from '../hooks/reactQuery/useCreateUser';
import CreateUserModal from '../components/modals/CreateUserModal';
import useChangePassword from '../hooks/reactQuery/useChangeUserPassword';
import EditPassword from '../components/modals/EditPasswordModal';
import NewOrders from './NewOrders';
import KittingOrders from './KittingOrders';
import DeliveredOrders from './DeliveredOrders';
import ShippingOrders from './ShippingOrders';
import ThunderbirdOrders from './ThunderbirdOrders';
import ThunderbirdOrderDetailsModal from '../components/modals/ThunderbirdOrderDetailsModal';
import useGetOrderCount from '../hooks/reactQuery/useGetOrdersCount';
import pagesSlug from '../constant/pages-slug';
import { APP_NAME, APP_VERSION } from '../constant/app-meta';
import localTimeZoneLabel from '../utils/localTimeZone';
import useSessionGuard from '../hooks/useSessionGuard';
import UpdateUserPassword from '../components/modals/UpdateUserPasswordModal';
import useChangePasswordByAdmin from '../hooks/reactQuery/useChangeUserPasswordByAdmin';
import useProfile from '../hooks/useProfile';
import {
  clearTelemetryUser,
  setTelemetryContext,
  setTelemetryUser,
  track,
  useModalTelemetry,
  useRequestFailureTelemetry,
  useRouteTelemetry,
} from '../telemetry';

interface TableRowState {
  class: string;
  orderId: number;
  isActive: boolean;
}

const getTitle = (slug: string) => {
  if (slug === pagesSlug.users) {
    return 'User Management';
  }
  if (slug === pagesSlug.settings) {
    return 'Settings';
  }
  if (slug === pagesSlug.newOrders) {
    return 'New Orders';
  }
  if (slug === pagesSlug.shipping) {
    return 'Shipping Orders';
  }
  if (slug === pagesSlug.delivered) {
    return 'Order in Delivery';
  }
  if (slug === pagesSlug.kitting) {
    return 'Product Kitting Orders';
  }
  if (slug === pagesSlug.thunderbird) {
    return 'Thunderbird Orders';
  }
  if (slug === pagesSlug.askAi) {
    return 'Ask AI';
  }
  if (slug === pagesSlug.schedules) {
    return 'Schedules';
  }
  return 'Orders';
};

// Order panels open in a centred pop-up over the list; everything else stays a slide-over drawer.
const POPUP_MODALS = ['order', 'thunderbird-order', 'create-thunderbird-order'];
// URL query keys that carry the open order's PID so a refresh or a shared link reopens it.
const URL_KEY: Record<string, string> = {
  order: 'order',
  'thunderbird-order': 'tb',
};

const readPendingOpen = (): { kind: OpenableOrderKind; pid: string } | null => {
  const params = new URLSearchParams(window.location.search);
  const order = params.get('order');
  if (order) return { kind: 'order', pid: order };
  const tb = params.get('tb');
  if (tb) return { kind: 'thunderbird-order', pid: tb };
  return null;
};

const ORDER_PAGES = [
  'dashboard',
  pagesSlug.newOrders,
  pagesSlug.kitting,
  pagesSlug.shipping,
  pagesSlug.delivered,
  pagesSlug.thunderbird,
];

// The order list pages carry the list tools in the top bar (refresh time, auto-refresh,
// Export, Create Thunderbird Order). The Thunderbird page is an order page for its title
// and count, but it lists Thunderbird's own orders and gets none of those tools.
const ORDER_LIST_PAGES = ORDER_PAGES.filter(
  (slug) => slug !== pagesSlug.thunderbird,
);

const getMenuItem = (path: string) => {
  if (path.includes('users')) {
    return pagesSlug.users;
  }
  if (path.includes('settings')) {
    return pagesSlug.settings;
  }
  if (path.includes('new-orders')) {
    return pagesSlug.newOrders;
  }
  if (path.includes('kitting-orders')) {
    return pagesSlug.kitting;
  }
  if (path.includes('shipping-orders')) {
    return pagesSlug.shipping;
  }
  if (path.includes('delivery-orders')) {
    return pagesSlug.delivered;
  }
  if (path.includes('thunderbird-orders')) {
    return pagesSlug.thunderbird;
  }
  if (path.includes('ask-ai')) {
    return pagesSlug.askAi;
  }
  if (path.includes('schedules')) {
    return pagesSlug.schedules;
  }
  return 'dashboard';
};

const getModal = (
  modalType: string | undefined,
  saveData: (data: any) => void,
  data: any,
  isModalOpen: boolean,
  isLoading: boolean,
  onClose?: () => void,
) => {
  if (modalType === 'order') {
    return (
      <OrderDetailsModal
        orderData={data}
        isLoading={isLoading}
        onClose={onClose}
      />
    );
  }
  if (modalType === 'user') {
    return (
      <UserDetailsModal onSubmit={saveData} data={data} isLoading={isLoading} />
    );
  }
  if (modalType === 'update-user-password') {
    return (
      <UpdateUserPassword
        onSubmit={saveData}
        data={data}
        isModalOpen={isModalOpen}
        isLoading={isLoading}
      />
    );
  }
  if (modalType === 'create-user') {
    return (
      <CreateUserModal
        onSubmit={saveData}
        isModalOpen={isModalOpen}
        isLoading={isLoading}
      />
    );
  }
  if (modalType === 'create-order') {
    return <CreateOrderModal onSubmit={saveData} isLoading={isLoading} />;
  }
  if (modalType === 'create-thunderbird-order') {
    return (
      <CreateThunderbirdOrderModal
        onSubmit={saveData}
        isLoading={isLoading}
        onClose={onClose}
      />
    );
  }
  if (modalType === 'thunderbird-order') {
    return (
      <ThunderbirdOrderDetailsModal
        orderData={data}
        isLoading={isLoading}
        onClose={onClose}
      />
    );
  }
  if (modalType === 'settings') {
    return (
      <EditPassword
        onSubmit={saveData}
        data={data}
        isModalOpen={isModalOpen}
        isLoading={isLoading}
      />
    );
  }
  return null;
};

const Auth: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { user, isAuthenticated, logout } = useAuth0();
  useSessionGuard();
  useRouteTelemetry();
  useRequestFailureTelemetry();
  const { role } = useProfile();
  useEffect(() => {
    if (user?.sub) setTelemetryUser(user.sub, user.email, user.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]);
  useEffect(() => {
    setTelemetryContext({ role: role || undefined });
  }, [role]);
  // Which page is showing, from the URL, so back/forward keep the top bar in step.
  const selectedMenuItem = getMenuItem(location.pathname);
  const [modalType, setModalType] = useState<undefined | string>('');
  const [modalData, setModalData] = useState<any>({});
  const { mutate: updateUser, isLoading: isUpdateUserLoading } =
    useUserUpdate(undefined);
  const { mutate: createOrder, isLoading: isCreateOrderLoading } =
    useCreateOrder();
  const {
    mutate: createThunderbirdOrder,
    isLoading: isCreateThunderbirdOrderLoading,
  } = useCreateThunderbirdOrder();
  const { mutate: createUser, isLoading: isCreateUserLoading } =
    useCreateUser();
  const { mutate: changePassword, isLoading: isChangePasswordLoading } =
    useChangePassword();
  const { data: orderCountData, isLoading: isGetOrderCountLoading } =
    useGetOrderCount();
  const { mutate: changeUserPassword, isLoading: isChangeUserPasswordLoading } =
    useChangePasswordByAdmin();

  const isloading =
    isUpdateUserLoading ||
    isCreateOrderLoading ||
    isCreateThunderbirdOrderLoading ||
    isChangePasswordLoading ||
    isCreateUserLoading ||
    isChangeUserPasswordLoading ||
    isGetOrderCountLoading;

  const [tableRowClass, setTableRowClass] = useState<TableRowState | null>(
    null,
  );

  const handleMenuItemClick = (menuItem: string) => {
    if (menuItem !== 'logout') return;
    track('logout_clicked', {});
    clearTelemetryUser();
    // Without an explicit returnTo, Auth0 ends the logout on its own hosted page, which lives on
    // the canonical tenant hostname rather than the branded one - so signing out looked like it
    // dropped the user back onto the old login domain. Send them back to this app instead; the
    // origin is already an allowed logout URL for every environment.
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const [showSidebar, setShowSidebar] = useState(window.innerWidth >= 980);

  useEffect(() => {
    const handleResize = () => {
      setShowSidebar(window.innerWidth >= 980);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  // Whether the open order panel came from a row click or the ?order= / ?tb= deep link.
  const openedVia = useRef<'row' | 'url'>('row');
  useModalTelemetry({
    isOpen: isModalOpen,
    modal: modalType,
    orderPid: modalData?.pid,
    orderStatus: modalData?.status,
    via: openedVia.current,
  });

  const handleModalOpen = (data: any) => {
    openedVia.current = 'row';
    setModalData(data?.data);
    setModalType(data?.modal);
    if (data?.isRow) {
      setIsModalOpen(false);
    }
    if (
      tableRowClass &&
      tableRowClass.orderId === data?.data?.id &&
      tableRowClass.isActive
    ) {
      setIsModalOpen(false);
      setTableRowClass({
        class: '',
        orderId: data?.data?.id,
        isActive: false,
      });
    } else {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = (
    data: any,
    isCloseButton = false,
    isOutSide = false,
  ) => {
    if (isCloseButton) {
      setTableRowClass({ class: '', isActive: false, orderId: 0 });
      setIsModalOpen(false);
    } else {
      setIsModalOpen(false);
    }
  };

  const saveData = (data: any) => {
    if (modalType === 'user') {
      updateUser(data, {
        onSuccess: () => {
          track('user_updated', {});
          toast.success(`User Updated`);
          setIsModalOpen(false);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update`);
        },
      });
    }
    if (modalType === 'update-user-password') {
      changeUserPassword(data, {
        onSuccess: () => {
          track('password_changed', { byAdmin: true });
          toast.success(`User Password Updated`);
          setIsModalOpen(false);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update`);
        },
      });
    }
    if (modalType === 'create-user') {
      createUser(data, {
        onSuccess: () => {
          track('user_created', {});
          toast.success(`User Created`);
          setIsModalOpen(false);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to create`);
        },
      });
    }
    if (modalType === 'create-order') {
      createOrder(data, {
        onSuccess: () => {
          toast.success(`Order Created`);
          setIsModalOpen(false);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to create`);
        },
      });
    }
    if (modalType === 'create-thunderbird-order') {
      createThunderbirdOrder(data, {
        onSuccess: () => {
          track('thunderbird_order_created', {});
          toast.success(`Thunderbird Order Created`);
          setIsModalOpen(false);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to create`);
        },
      });
    }
    if (modalType === 'settings') {
      changePassword(data, {
        onSuccess: () => {
          track('password_changed', { byAdmin: false });
          toast.success(`Password updated`);
          setIsModalOpen(false);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update`);
        },
      });
    }
  };
  const showPopup = isModalOpen && POPUP_MODALS.includes(modalType || '');
  let popupLabel = 'Order';
  if (modalType === 'create-thunderbird-order') {
    popupLabel = 'Create Thunderbird order';
  } else if (modalData?.pid) {
    popupLabel = `Order ${modalData.pid}`;
  }

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A Bootstrap confirmation (Cancel order) owns Escape while it is up.
      if ((e.target as Element | null)?.closest?.('.modal')) return;
      handleModalClose(modalData, true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, modalData]);

  // Reopen the order named in the URL (once, on load)
  const [pendingOpen, setPendingOpen] = useState(readPendingOpen);
  const { data: pendingItem, isFetched: pendingFetched } = useOrderByPid(
    pendingOpen?.pid,
    pendingOpen?.kind,
  );
  useEffect(() => {
    if (!pendingOpen || !pendingFetched) return;
    if (pendingItem) {
      openedVia.current = 'url';
      setModalData(pendingItem);
      setModalType(pendingOpen.kind);
      setIsModalOpen(true);
      setTableRowClass({
        class: 'is-selected',
        orderId: pendingItem.id,
        isActive: true,
      });
    } else {
      toast.info(`Order ${pendingOpen.pid} was not found`);
    }
    setPendingOpen(null);
  }, [pendingOpen, pendingItem, pendingFetched]);

  // Keep ?order= / ?tb= in step with the open panel
  useEffect(() => {
    if (pendingOpen) return;
    const params = new URLSearchParams(location.search);
    const key = modalType ? URL_KEY[modalType] : undefined;
    const pid = modalData?.pid ? String(modalData.pid) : '';
    const before = params.toString();
    if (isModalOpen && key && pid) {
      Object.values(URL_KEY).forEach((k) => {
        if (k !== key) params.delete(k);
      });
      params.set(key, pid);
    } else {
      Object.values(URL_KEY).forEach((k) => params.delete(k));
    }
    const after = params.toString();
    if (after !== before) history.replace({ search: after });
  }, [
    isModalOpen,
    modalType,
    modalData?.pid,
    location.search,
    pendingOpen,
    history,
  ]);

  let primaryAction: PrimaryAction | undefined;
  if (ORDER_LIST_PAGES.includes(selectedMenuItem)) {
    primaryAction = {
      label: 'Create Thunderbird Order',
      onClick: () => handleModalOpen({ modal: 'create-thunderbird-order' }),
    };
  } else if (selectedMenuItem === pagesSlug.users) {
    primaryAction = {
      label: 'Create User',
      onClick: () => handleModalOpen({ modal: 'create-user' }),
    };
  }
  let topBarNoun: string | undefined;
  if (ORDER_PAGES.includes(selectedMenuItem)) topBarNoun = 'order';
  if (selectedMenuItem === pagesSlug.users) topBarNoun = 'user';

  return isAuthenticated && user ? (
    <PageMetaProvider>
      <div className="dashboard-container">
        <RightSideModal
          isOpen={isModalOpen && !showPopup}
          onClose={handleModalClose}
          data={modalData}
        >
          {getModal(modalType, saveData, modalData, isModalOpen, isloading)}
        </RightSideModal>
        {showPopup ? (
          <OrderPopup
            label={popupLabel}
            size={modalType === 'create-thunderbird-order' ? 'md' : 'lg'}
            onClose={() => handleModalClose(modalData, true)}
          >
            {getModal(
              modalType,
              saveData,
              modalData,
              isModalOpen,
              isloading,
              () => handleModalClose(modalData, true),
            )}
          </OrderPopup>
        ) : null}
        <Sidebar
          showSidebar={showSidebar}
          selectedMenuItem={selectedMenuItem}
          onMenuItemClick={handleMenuItemClick}
          aggregates={orderCountData}
        />

        <div className="dashboard-area">
          <TopBar
            title={getTitle(selectedMenuItem)}
            noun={topBarNoun}
            showMenuButton={!showSidebar || window.innerWidth < 980}
            onToggleSidebar={toggleSidebar}
            primaryAction={primaryAction}
            showExport={ORDER_LIST_PAGES.includes(selectedMenuItem)}
            showRefresh={ORDER_LIST_PAGES.includes(selectedMenuItem)}
          />
          <Switch>
            <Route key="new-orders" path="/new-orders">
              <NewOrders
                handleModalOpen={handleModalOpen}
                rowDetails={tableRowClass}
                setRowDetails={setTableRowClass}
              />
            </Route>
            <Route path="/kitting-orders">
              <KittingOrders
                handleModalOpen={handleModalOpen}
                rowDetails={tableRowClass}
                setRowDetails={setTableRowClass}
              />
            </Route>
            <Route path="/delivery-orders">
              <DeliveredOrders
                handleModalOpen={handleModalOpen}
                rowDetails={tableRowClass}
                setRowDetails={setTableRowClass}
              />
            </Route>
            <Route path="/shipping-orders">
              <ShippingOrders
                handleModalOpen={handleModalOpen}
                rowDetails={tableRowClass}
                setRowDetails={setTableRowClass}
              />
            </Route>
            <Route path="/thunderbird-orders">
              <ThunderbirdOrders
                handleModalOpen={handleModalOpen}
                rowDetails={tableRowClass}
                setRowDetails={setTableRowClass}
              />
            </Route>
            <Route path="/users">
              <Users handleModalOpen={handleModalOpen} />
            </Route>
            <Route path="/settings">
              <UserProfileSettings handleModalOpen={handleModalOpen} />
            </Route>
            <Route path="/ask-ai">
              <AskAi />
            </Route>
            <Route path="/schedules">
              <Schedules />
            </Route>
            <Route exact key="dashboard" path="/">
              <Dashboard
                orderCounts={orderCountData}
                handleModalOpen={handleModalOpen}
                rowDetails={tableRowClass}
                setRowDetails={setTableRowClass}
              />
            </Route>
          </Switch>
          <footer className="app-footer">
            <span>
              © {new Date().getFullYear()} innovaRXI · {APP_NAME}
              {APP_VERSION ? ` v${APP_VERSION}` : ''}
            </span>
            <span>All times are your local time, {localTimeZoneLabel()}.</span>
          </footer>
        </div>
      </div>
    </PageMetaProvider>
  ) : null;
};

export default Auth;
